// @ts-nocheck
// CAMINHO: src/pages/CaseDetailPage.tsx
// VERSÃO: 3.0 - LAYOUT DE DASHBOARD COM VISÃO GERAL

import { useState, useEffect, useCallback } from 'react';
import { CaseCourtCosts } from '@/components/cases/CaseCourtCosts';
import { CaseFinancialSettings } from '@/components/cases/CaseFinancialSettings';
import { CaseNotes } from '@/components/cases/CaseNotes';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { format, parseISO } from 'date-fns';
import { 
  ArrowLeft, 
  Edit, 
  Users, 
  Scale, 
  Calendar, 
  FileText, 
  Landmark, 
  Clock,
  BrainCircuit,
  Sparkles,
  PlusCircle,
  Trash2,
  Crown,
  Building2,
  UserPlus,
  Loader2,
  RefreshCcw,
  Archive,
  DollarSign
} from 'lucide-react';
import { EditCaseInfoModal } from '@/components/cases/EditCaseInfoModal';
import { MovementsTab } from '@/components/cases/MovementsTab';
import { CaseTasks } from '@/components/tasks/CaseTasks';
import { CaseDocuments } from '@/components/documents/CaseDocuments';
import { CaseDeadlines } from '@/components/tasks/CaseDeadlines';
import { AddPartiesModal } from '@/components/cases/AddPartiesModal';
import { DatajudInfo } from '@/components/cases/DatajudInfo';
import { ArchiveCaseModal } from '@/components/cases/ArchiveCaseModal';
import { datajudApi } from '@/services/datajudAPI';
import { CaseProcessTimeline } from '@/components/cases/CaseProcessTimeline';

interface Case {
    id: string;
    title: string;
    case_number_cnj?: string;
    case_number?: string;
    case_type?: string;
    status?: string;
    priority?: string;
    court?: string;
    filing_date?: string;
    deadline?: string;
    description?: string;
    lawyer_responsible?: string;
    client_id?: string;
    is_litisconsorcio?: boolean;
    clients?: { name: string; email: string; id: string };
    datajud_json?: any;
    last_datajud_sync?: string;
    case_value?: number;
    fee_configuration_json?: any;
    benefit_value_type?: string;
    is_benefit_undefined?: boolean;
    undefined_benefit_first_installments_count?: number;
    undefined_benefit_first_installment_value?: number;
    has_retroactive_payment?: boolean;
    retroactive_payment_value?: number;
    retroactive_payment_type?: string;
    retroactive_payment_date?: string;
    retroactive_payment_date_pending?: boolean;
    charge_on_retroactive?: boolean;
    charge_on_installments?: boolean;
    charge_on_both?: boolean;
    is_pro_bono?: boolean;
    has_initial_fee?: boolean;
    initial_fee_amount?: number;
    initial_fee_type?: string;
    initial_fee_base_value?: number;
    initial_fee_payment_status?: string;
    has_success_fee?: boolean;
    success_fee_type?: string;
    success_fee_value?: number;
    success_fee_trigger?: string;
    success_fee_calculation_base?: string;
    benefit_payment_type?: string;
    benefit_installment_count?: number;
    benefit_installment_value?: number;
    benefit_first_payment_date?: string;
    benefit_first_payment_date_pending?: boolean;
}

export const CaseDetailPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { toast } = useToast();
    const [caseDetails, setCaseDetails] = useState<Case | null>(null);
    const [caseClients, setCaseClients] = useState<Array<{ id: string; name: string; email: string; is_primary: boolean }>>([]);
    const [opposingParties, setOpposingParties] = useState<Array<{ id: string; name: string; email: string; phone: string; document_number: string; is_primary: boolean }>>([]);
    const [datajudData, setDatajudData] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSyncing, setIsSyncing] = useState(false);
    const [activeTab, setActiveTab] = useState('overview');
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isAddPartiesModalOpen, setIsAddPartiesModalOpen] = useState(false);
    const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);

    const fetchData = useCallback(async () => {
        if (!id) return;

        setIsLoading(true);
        try {
            // Buscar dados do caso
            const { data, error } = await supabase
                .from('cases')
                .select(`
                    *,
                    clients:client_id(id, name, email)
                `)
                .eq('id', id)
                .single();

            if (error) throw error;

            console.log('Dados do processo carregados: ', data);
            setCaseDetails(data);

            // Carregar dados do Datajud se disponível
            if ((data as any).datajud_json) {
                setDatajudData((data as any).datajud_json);
            }

            // Sempre tentar carregar clientes da tabela case_clients
            console.log('Carregando clientes da tabela case_clients...');

            try {
                // Usar a função RPC para carregar todos os clientes
                const { data: clientsData, error: clientsError } = await supabase
                    .rpc('get_case_clients' as any, { case_id_param: id });

                console.log('Resultado da função RPC:', clientsData);
                console.log('Erro na função RPC:', clientsError);

                if (clientsError) {
                    console.error('Erro ao carregar clientes:', clientsError);
                    // Fallback: usar apenas o cliente principal
                    if (data.client_id && data.clients) {
                        setCaseClients([{
                            id: data.clients.id,
                            name: data.clients.name,
                            email: data.clients.email,
                            is_primary: true
                        }]);
                    } else {
                        setCaseClients([]);
                    }
                } else if (clientsData && Array.isArray(clientsData) && clientsData.length > 0) {
                    const formattedClients = clientsData.map((item: any) => ({
                        id: item.client_id,
                        name: item.client_name || 'Cliente não encontrado',
                        email: item.client_email || '',
                        is_primary: item.is_primary
                    }));
                    console.log('Clientes carregados:', formattedClients);
                    setCaseClients(formattedClients);
                } else {
                    console.log('Nenhum cliente encontrado via RPC, usando cliente principal');
                    // Fallback: usar apenas o cliente principal
                    if (data.client_id && data.clients) {
                        setCaseClients([{
                            id: data.clients.id,
                            name: data.clients.name,
                            email: data.clients.email,
                            is_primary: true
                        }]);
                    } else {
                        setCaseClients([]);
                    }
                }
            } catch (error: any) {
                console.error('Erro ao carregar clientes:', error);
                // Fallback: usar apenas o cliente principal
                if (data.client_id && data.clients) {
                    setCaseClients([{
                        id: data.clients.id,
                        name: data.clients.name,
                        email: data.clients.email,
                        is_primary: true
                    }]);
                } else {
                    setCaseClients([]);
                }
            }

            // Carregar partes contrárias
            try {
                const { data: opposingData, error: opposingError } = await supabase
                    .rpc('get_case_opposing_parties' as any, { case_id_param: id });

                if (opposingError) {
                    console.error('Erro ao carregar partes contrárias:', opposingError);
                    setOpposingParties([]);
                } else if (opposingData && Array.isArray(opposingData)) {
                    const formattedOpposing = opposingData.map((item: any) => ({
                        id: item.opposing_party_id,
                        name: item.opposing_party_name || 'Parte não encontrada',
                        email: item.opposing_party_email || '',
                        phone: item.opposing_party_phone || '',
                        document_number: item.opposing_party_document || '',
                        is_primary: item.is_primary
                    }));
                    setOpposingParties(formattedOpposing);
                } else {
                    setOpposingParties([]);
                }
            } catch (error: any) {
                console.error('Erro ao carregar partes contrárias:', error);
                setOpposingParties([]);
            }

        } catch (error: any) {
            console.error('Erro ao carregar dados do processo:', error);
            toast({ title: "Erro", description: "Erro ao carregar dados do processo", variant: "destructive" });
        } finally {
            setIsLoading(false);
        }
    }, [id, toast]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Ao abrir o processo, a movimentação nova passa a contar como vista (sai do sininho e da lista)
    useEffect(() => {
        const c: any = caseDetails;
        if (!c?.id || !c.movement_update_at) return;
        if (c.movement_seen_at && new Date(c.movement_seen_at) >= new Date(c.movement_update_at)) return;
        (supabase as any).from('cases').update({ movement_seen_at: new Date().toISOString() }).eq('id', c.id).then(() => {});
    }, [caseDetails?.id]);

    const handleSyncProcess = async () => {
        if (!caseDetails?.case_number_cnj) {
            toast({ title: "Erro", description: "Número do processo não encontrado para sincronização", variant: "destructive" });
            return;
        }

        setIsSyncing(true);
        try {
            console.log('Iniciando sincronização com Datajud para:', caseDetails.case_number_cnj);
            
            const response = await datajudApi.searchByProcessNumber(caseDetails.case_number_cnj);
            
            if (response.success && response.data && response.data.length > 0) {
                const processData = response.data[0];
                console.log('Dados do Datajud recebidos:', processData);
                
                const now = new Date().toISOString();
                
                // Atualizar dados do processo no banco
                const { error: updateError } = await supabase
                    .from('cases')
                    .update({ 
                        datajud_json: processData,
                        last_datajud_sync: now,
                        updated_at: now
                    })
                    .eq('id', caseDetails.id);

                if (updateError) {
                    console.error('Erro ao atualizar dados no banco:', updateError);
                    throw new Error('Erro ao salvar dados do Datajud');
                }

                // Atualizar estado local
                setDatajudData(processData);
                setCaseDetails(prev => prev ? { 
                    ...prev, 
                    datajud_json: processData,
                    last_datajud_sync: now
                } : null);
                
                toast({ 
                    title: "Sincronização Concluída!", 
                    description: `Processo sincronizado com ${processData.movimentacoes?.length || 0} andamentos` 
                });
                
                console.log('Sincronização concluída com sucesso');
            } else {
                console.log('Nenhum dado encontrado no Datajud');
                toast({ 
                    title: "Nenhum Dado Encontrado", 
                    description: "Não foram encontrados dados para este processo no Datajud", 
                    variant: "destructive" 
                });
            }
        } catch (error: any) {
            console.error('Erro na sincronização:', error);
            toast({ 
                title: "Erro na Sincronização", 
                description: error.message || "Erro ao sincronizar com o Datajud", 
                variant: "destructive" 
            });
        } finally {
            setIsSyncing(false);
        }
    };

    const handleNavigateToAI = () => {
        navigate('/ai-analysis', { 
            state: { 
                caseData: caseDetails,
                caseId: id 
            } 
        });
    };

    const getStatusColor = (status: string) => "bg-gray-400"; // Placeholder

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary mx-auto"></div>
                    <p className="mt-4 text-muted-foreground">Carregando dados do processo...</p>
                </div>
            </div>
        );
    }

    if (!caseDetails) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                    <h2 className="text-xl font-semibold mb-2">Processo não encontrado</h2>
                    <p className="text-muted-foreground mb-4">O processo solicitado não foi encontrado.</p>
                    <Button onClick={() => navigate('/cases')}>
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Voltar aos Processos
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto p-6 space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" onClick={() => navigate('/cases')}>
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Voltar
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold">{caseDetails.title}</h1>
                        <p className="text-muted-foreground">
                            {caseDetails.case_number_cnj || caseDetails.case_number || 'Sem número'}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button 
                        variant="outline" 
                        onClick={() => setIsArchiveModalOpen(true)}
                        className="bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100"
                    >
                        <Archive className="w-4 h-4 mr-2" />
                        Arquivar Processo
                    </Button>
                    <Button onClick={() => setIsEditModalOpen(true)}>
                        <Edit className="w-4 h-4 mr-2" />
                        Editar Informações
                    </Button>
                </div>
            </div>

            {/* Modal de Edição */}
            <EditCaseInfoModal
                caseDetails={caseDetails}
                isOpen={isEditModalOpen}
                onOpenChange={setIsEditModalOpen}
                onSuccess={fetchData}
            />

            {/* Corpo da Página - Layout de 2 Colunas */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                
                {/* Coluna Principal (Abas) */}
                <div className="lg:col-span-2">
                    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                        <TabsList>
                            <TabsTrigger value="overview">Visão Geral</TabsTrigger>
                            <TabsTrigger value="movements">Andamentos</TabsTrigger>
                            <TabsTrigger value="parties">Partes</TabsTrigger>
                            <TabsTrigger value="tasks">Tarefas</TabsTrigger>
                            <TabsTrigger value="documents">Documentos</TabsTrigger>
                            <TabsTrigger value="financial">Financeiro</TabsTrigger>
                            <TabsTrigger value="ai">Análise IA</TabsTrigger>
                        </TabsList>
                        
                        {/* Aba Visão Geral */}
                        <TabsContent value="overview" className="mt-4 space-y-6">
                            {/* Timeline Horizontal do Processo */}
                            <CaseProcessTimeline
                                caseId={id!}
                                onAddDocumentClick={() => {
                                    const el = document.getElementById('documents-section');
                                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                }}
                            />
                            <Card>
                                <CardHeader><CardTitle>Últimos Andamentos</CardTitle></CardHeader>
                                <CardContent>
                                    <MovementsTab movements={datajudData?.movimentacoes?.slice(0, 3) || []} caseDetails={caseDetails} onDataUpdate={fetchData} />
                                </CardContent>
                            </Card>
                            <Card id="documents-section">
                                <CardHeader><CardTitle>Documentos Recentes</CardTitle></CardHeader>
                                <CardContent>
                                    <CaseDocuments caseId={id!} />
                                </CardContent>
                            </Card>
                        </TabsContent>

                        {/* Outras Abas */}
                        <TabsContent value="movements" className="mt-4">
                            <MovementsTab movements={datajudData?.movimentacoes || []} caseDetails={caseDetails} onDataUpdate={fetchData} />
                        </TabsContent>
                        <TabsContent value="parties" className="mt-4">
                            <PartiesTab 
                                clients={caseClients} 
                                opposingParties={opposingParties}
                                caseDetails={caseDetails} 
                                onDataUpdate={fetchData} 
                                onOpenAddModal={() => setIsAddPartiesModalOpen(true)}
                            />
                        </TabsContent>
                        <TabsContent value="tasks" className="mt-4">
                            <CaseTasks caseId={id!} />
                        </TabsContent>
                        <TabsContent value="documents" className="mt-4">
                            <CaseDocuments caseId={id!} />
                        </TabsContent>
                        <TabsContent value="financial" className="mt-4">
                            {caseDetails?.id && (
                                <div className="mb-4">
                                    <CaseCourtCosts caseId={caseDetails.id} />
                                </div>
                            )}
                            {caseDetails?.id && (
                                <CaseFinancialSettings key={caseDetails.id} caseDetails={caseDetails} onSaved={fetchData} />
                            )}
                        </TabsContent>
                        <TabsContent value="ai" className="mt-4">
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2"><BrainCircuit /> Centro de Análise com IA</CardTitle>
                                    <CardDescription>Potencialize sua estratégia analisando os dados deste processo com a ajuda da nossa IA.</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <p className="mb-4 text-sm">
                                        Ao clicar no botão abaixo, você será levado ao Centro de IA com os principais dados deste processo já pré-carregados,
                                        prontos para você fazer perguntas, solicitar resumos, identificar riscos e elaborar os próximos passos.
                                    </p>
                                    <Button onClick={handleNavigateToAI}>
                                        <Sparkles className="w-4 h-4 mr-2" />
                                        Analisar no Centro de IA
                                    </Button>
                                </CardContent>
                            </Card>
                        </TabsContent>
                    </Tabs>
                </div>

                {/* Coluna Lateral (Informações Fixas) */}
                <aside className="space-y-6 lg:col-span-1">
                    <Card>
                        <CardHeader><CardTitle>Status e Prioridade</CardTitle></CardHeader>
                        <CardContent className="flex gap-2">
                            <Badge className={`${getStatusColor(caseDetails.status || '')}`}>{caseDetails.status}</Badge>
                            <Badge variant="outline">Prioridade {caseDetails.priority}</Badge>
                        </CardContent>
                    </Card>

                    {/* Observações livres do processo */}
                    <CaseNotes caseId={caseDetails.id} />
                    
                    {/* Componente de informações do Datajud */}
                    <DatajudInfo
                        datajudData={datajudData}
                        lastSync={caseDetails.last_datajud_sync}
                        isSyncing={isSyncing}
                        onSync={handleSyncProcess}
                        caseNumber={caseDetails.case_number_cnj}
                    />
                    
                    <Card>
                        <CardHeader><CardTitle>Informações Chave</CardTitle></CardHeader>
                        <CardContent className="space-y-3 text-sm">
                            <div className="flex items-start gap-3"><FileText className="w-4 h-4 mt-1 text-muted-foreground" /><p><strong>Tipo:</strong> {caseDetails.case_type}</p></div>
                            <div className="flex items-start gap-3"><Landmark className="w-4 h-4 mt-1 text-muted-foreground" /><p><strong>Tribunal:</strong> {caseDetails.court}</p></div>
                            <div className="flex items-start gap-3"><Calendar className="w-4 h-4 mt-1 text-muted-foreground" /><p><strong>Autuação:</strong> {caseDetails.filing_date ? format(parseISO(caseDetails.filing_date), 'dd/MM/yyyy') : 'N/A'}</p></div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardHeader><CardTitle>Próximos Prazos</CardTitle></CardHeader>
                        <CardContent>
                            <CaseDeadlines caseId={id!} />
                        </CardContent>
                    </Card>
                </aside>
            </div>

            {/* Modal de Adicionar Partes */}
            <AddPartiesModal
                isOpen={isAddPartiesModalOpen}
                onClose={() => setIsAddPartiesModalOpen(false)}
                caseId={id!}
                onDataUpdate={fetchData}
            />

            {/* Modal de Arquivar Processo */}
            <ArchiveCaseModal
                isOpen={isArchiveModalOpen}
                onClose={() => setIsArchiveModalOpen(false)}
                caseId={id!}
                caseTitle={caseDetails.title}
                onSuccess={() => {
                    toast({ 
                        title: "Processo Arquivado!", 
                        description: "Redirecionando para a lista de processos..." 
                    });
                    setTimeout(() => navigate('/cases'), 2000);
                }}
            />
        </div>
    );
};

const PartiesTab = ({ 
    clients, 
    opposingParties,
    caseDetails, 
    onDataUpdate,
    onOpenAddModal
}: { 
    clients: Array<{ id: string; name: string; email: string; is_primary: boolean }>;
    opposingParties: Array<{ id: string; name: string; email: string; phone: string; document_number: string; is_primary: boolean }>;
    caseDetails: Case;
    onDataUpdate: () => void;
    onOpenAddModal: () => void;
}) => {
    const { toast } = useToast();

    const handleRemoveClient = async (clientId: string) => {
        if (!window.confirm("Tem certeza que deseja remover este cliente do processo?")) return;

        try {
            const { error } = await supabase
                .rpc('remove_client_from_case' as any, {
                    case_id_param: caseDetails.id,
                    client_id_param: clientId
                });

            if (error) throw error;
            toast({ title: "Sucesso!", description: "Cliente removido do processo." });
            onDataUpdate();
        } catch (error: any) {
            console.error('Erro ao remover cliente:', error);
            toast({ title: "Erro", description: "Erro ao remover cliente do processo", variant: "destructive" });
        }
    };

    const handleRemoveOpposingParty = async (opposingPartyId: string) => {
        if (!window.confirm("Tem certeza que deseja remover esta parte contrária do processo?")) return;

        try {
            const { error } = await supabase
                .rpc('remove_opposing_party_from_case' as any, {
                    case_id_param: caseDetails.id,
                    opposing_party_id_param: opposingPartyId
                });

            if (error) throw error;
            toast({ title: "Sucesso!", description: "Parte contrária removida do processo." });
            onDataUpdate();
        } catch (error: any) {
            console.error('Erro ao remover parte contrária:', error);
            toast({ title: "Erro", description: "Erro ao remover parte contrária do processo", variant: "destructive" });
        }
    };

    return (
        <div className="space-y-6">
            {/* Polo Ativo */}
            <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                        <UserPlus className="w-4 h-4" />
                        Polo Ativo (Clientes)
                        {caseDetails.is_litisconsorcio && (
                            <Badge variant="outline">Litisconsórcio</Badge>
                        )}
                    </CardTitle>
                    <Button size="sm" onClick={onOpenAddModal}>
                        <PlusCircle className="w-4 h-4 mr-2" />
                        Adicionar Cliente
                    </Button>
                </CardHeader>
                <CardContent className="space-y-3">
                    {clients.length > 0 ? (
                        clients.map((client, index) => (
                            <div key={`cliente-${index}`} className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                                        <UserPlus className="w-5 h-5 text-primary" />
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <p className="font-medium">{client.name}</p>
                                            {client.is_primary && (
                                                <Badge variant="secondary" className="flex items-center gap-1">
                                                    <Crown className="w-3 h-3" />
                                                    Principal
                                                </Badge>
                                            )}
                                        </div>
                                        <p className="text-sm text-muted-foreground">{client.email}</p>
                                    </div>
                                </div>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleRemoveClient(client.id)}
                                    className="h-8 w-8"
                                >
                                    <Trash2 className="w-4 h-4 text-destructive" />
                                </Button>
                            </div>
                        ))
                    ) : (
                        <div className="text-center text-muted-foreground py-8">
                            <Users className="mx-auto w-12 h-12 mb-3 opacity-50" />
                            <p>Nenhuma parte no polo ativo.</p>
                            <p className="text-sm">Adicione clientes para começar.</p>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Polo Passivo */}
            <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        Polo Passivo (Partes Contrárias)
                    </CardTitle>
                    <Button size="sm" onClick={onOpenAddModal}>
                        <PlusCircle className="w-4 h-4 mr-2" />
                        Adicionar Parte Contrária
                    </Button>
                </CardHeader>
                <CardContent className="space-y-3">
                    {opposingParties.length > 0 ? (
                        opposingParties.map((party, index) => (
                            <div key={`parte-contraria-${index}`} className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-destructive/10 rounded-full flex items-center justify-center">
                                        <Building2 className="w-5 h-5 text-destructive" />
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <p className="font-medium">{party.name}</p>
                                            {party.is_primary && (
                                                <Badge variant="secondary" className="flex items-center gap-1">
                                                    <Crown className="w-3 h-3" />
                                                    Principal
                                                </Badge>
                                            )}
                                        </div>
                                        <div className="text-sm text-muted-foreground space-y-1">
                                            {party.email && <p>{party.email}</p>}
                                            {party.phone && <p>{party.phone}</p>}
                                            {party.document_number && <p>{party.document_number}</p>}
                                        </div>
                                    </div>
                                </div>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleRemoveOpposingParty(party.id)}
                                    className="h-8 w-8"
                                >
                                    <Trash2 className="w-4 h-4 text-destructive" />
                                </Button>
                            </div>
                        ))
                    ) : (
                        <div className="text-center text-muted-foreground py-8">
                            <Building2 className="mx-auto w-12 h-12 mb-3 opacity-50" />
                            <p>Nenhuma parte no polo passivo.</p>
                            <p className="text-sm">Adicione partes contrárias para começar.</p>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};